import m from './module';
import Event from '../model/Event';
import {
  getProvisionalIdentifierForType,
  mapObjectsByKey,
  createError,
} from '../util';

const getEventsForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/events';
const getSingleEventPath = '/event';
const objectType = 'Event';

const datetimeFieldsRelativeToEventTimezone = [
  'start_timestamp',
  'end_timestamp',
];
const datetimeFieldsRelativeToServerTimezone = [
  'created_at',
  'updated_at',
  'recap_approved_time',
  'recap_rejected_time',
  'payments_approved_time',
  'recap_completed_time',
  'recap_recompleted_time',
  'staffing_queue_sendtime',
  'claims_locked_at',
];

class EventService {
  constructor(
    $q,
    APIService,
    LogService,
    LocationVariableService,
    RoleVariableService,
    PositionClaimTypeService,
    DateTimeService,
    DataSyncService
  ) {
    "ngInject";
    this.$q = $q;
    this.APIService = APIService;
    this.LogService = LogService;
    this.LocationVariableService = LocationVariableService;
    this.RoleVariableService = RoleVariableService;
    this.PositionClaimTypeService = PositionClaimTypeService;
    this.DateTimeService = DateTimeService;
    this.DataSyncService = DataSyncService;
  }

  /**
   * Retrieve an event by ID
   * If there is a staff user logged in, this will defer to getByIdForStaffUserId()
   * If not, it will make the call directly to the event details API
   */
  getById(id, config = null) {
    const staffUserId = this.DataSyncService.staffUserId;
    if (staffUserId) {
      return this.getByIdForStaffUserId(id, staffUserId, config);
    } else {
      this.LogService.warn(`EventService.getById() not using staffUserId`, id);
      const path = getSingleEventPath;
      const factory = eventData => this.create(eventData);
      return this.DataSyncService.getObjectById(objectType, path, id, factory, config);
    }
  }

  /**
   * Retrieve an event by ID for a particular staff user.
   * This will only do a single HTTP request to pull all events for the staff user, then pick the specified event
   * by ID.
   */
  getByIdForStaffUser(id, staffUser, config = null) {
    if (!staffUser) {
      throw createError({
        error: 'MissingStaffUser',
        message: 'DataSyncService.staffUser is missing',
      });
    }

    const staffUserId = staffUser.id;
    const syncWindowDefinition = staffUser.syncWindowDefinition;
    const basePath = getEventsForStaffingUserPath.replace('{{staffing_users_id}}', staffUserId);
    const factory = eventData => this.create(eventData);
    config = config || {};
    config.debug = true;

    const path = this.APIService.addURLParamsFromObject(basePath, syncWindowDefinition.params);

    this.LogService.debug(`EventService.getByIdForStaffUser(${id}, ${staffUser.id}) syncWindowName=${syncWindowDefinition.name}; path: ${path}`);
    return this.DataSyncService.getObjectByIdFromMultiple(objectType, path, id, factory, config);
  }

  getByIdForStaffUserId(id, staffUserId, config = null) {
    let staffUser = this.DataSyncService.staffUser;
    if (!staffUser) {
      // We have a problem!
      this.LogService.error(`EventService.getByIdForStaffUserId() can't find staffUser on DataSyncService!`);
    } else if (staffUser.id !== staffUserId) {
      // This is unexpected...
      this.LogService.error(`EventService.getByIdForStaffUserId() staffUserId=${staffUserId} does not match DataSyncService.staffUser.id ${staffUser.id}`);
    }
    return this.getByIdForStaffUser(id, staffUser, config);
  }

  getAllForStaffUser(staffUser, config = null) {
    if (!staffUser) {
      throw createError({
        error: 'MissingStaffUser',
        message: 'DataSyncService.staffUser is missing',
      });
    }

    const staffUserId = staffUser.id;
    const syncWindowDefinition = staffUser.syncWindowDefinition;
    const basePath = getEventsForStaffingUserPath.replace('{{staffing_users_id}}', staffUserId);
    const factory = eventData => this.create(eventData);
    config = config || {};
    config.debug = true;

    const path = this.APIService.addURLParamsFromObject(basePath, syncWindowDefinition.params);

    return this.DataSyncService.getMultipleObjects(objectType, path, factory, config)
      .then(events => {
        events.forEach(event => this.LogService.debug(`EventService.getAllForStaffUserId() loaded event id=${event.id} start_timestamp=${event.start_timestamp}`));
        return events;
      });
  }

  getAllForStaffUserId(staffUserId, config = null) {
    const staffUser = this.DataSyncService.staffUser;
    if (!staffUser) {
      // We have a problem!
      this.LogService.error(`EventService.getAllForStaffUserId() can't find staffUser on DataSyncService!`);
    } else if (staffUser.id !== staffUserId) {
      // This is unexpected...
      this.LogService.error(`EventService.getAllForStaffUserId() staffUserId=${staffUserId} does not match DataSyncService.staffUser.id ${staffUser.id}`);
    }

    return this.getAllForStaffUser(staffUser, config);
  }

  create(data) {
    const eventPromise = this._doCreate(data);
    const eventId = data.id;
    return this.DataSyncService.setObjectById(objectType, eventId, eventPromise)
      .then(() => eventPromise)
      .catch(err => {
        this.LogService.warn(`EventService.create() caught error in setObjectById!`, err, `data:`, data, 'Swallowing and returning event');
        return eventPromise;
      });
  }

  getOrCreateWithData(data) {
    const id = data && data.id || null;
    return this.DataSyncService.getLocalObjectById(objectType, id)
      .then(obj => {
        // TODO: Add comparison to ensure that `obj` roughly matches `data`?
        if (obj) {
          return obj;
        }
        return this.create(data);
      });
  }

  _doCreate(data) {
    const promises = [];
    data = data || {};
    data.id = data.id || getProvisionalIdentifierForType(objectType);
    if (data.venue_timezone) {
      if (!data.timezoneName) {
        data.timezoneName = this.DateTimeService.verifyTimezoneName(data.venue_timezone);
      }
    }

    datetimeFieldsRelativeToEventTimezone.forEach(fieldName => {
      if (data[fieldName]) {
        data[fieldName] = this.DateTimeService.parseDateWithTimezone(data[fieldName], data.timezoneName);
      }
    });

    datetimeFieldsRelativeToServerTimezone.forEach(fieldName => {
      if (data[fieldName]) {
        data[fieldName] = this.DateTimeService.parseDateWithServerTimezone(data[fieldName]);
      }
    });

    const lvIds = data.location_variable_ids || [];
    const lvs = data.location_variables || [];

    if (lvs.length === lvIds.length) {
      // Use proper location variables
      promises.push(
        this.$q.all(lvs.map(lvData => this.LocationVariableService.createIsolated(lvData)))
          .then(createdLVs => {
            data.location_variables = createdLVs;
          })
      )
    } else {
      promises.push(
        this.LocationVariableService.getByIds(lvIds)
          .then(locationVariables => data.location_variables = locationVariables.filter(lv => !!lv))
      );
    }

    // Create ClaimTypes
    if (data.claim_types) {
      promises.push(
        this.$q
          .all(data.claim_types.map(claimTypeData => this.PositionClaimTypeService.create(claimTypeData)))
          .then(claimTypes => data.claim_types = claimTypes)
      )
    }

    return this.$q
      .all(promises)
      .then(() => new Event(data));
  }
}

export default EventService;

m.service('EventService', EventService);
